import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { Role } from "@/modules/roles/composables/useRoles";
import { useMutation, useQuery } from "@tanstack/vue-query";

interface Props {
  immediate?: boolean;
  errorHandling?: boolean;
}

const getInternalRoles = async (errorHandling = false): Promise<Role[]> => {
  const { data } = await axios.get<Role[]>("/api/internal/roles", {
    errorHandling,
  } as CustomAxiosRequestConfig);

  return data;
};

const createInternalRole = async (params: { name: string }) => {
  const { data } = await axios.post<Role>("/api/internal/roles", {
    name: params.name,
    title: params.name,
  });

  return data;
};

const deleteRoles = async (id: string | number) => {
  await axios.delete(`/api/internal/roles/${id}`);
};

const useInternalRoles = (props?: Props) => {
  const { immediate = true, errorHandling = false } = props || {};

  const { data, isLoading, refetch } = useQuery(
    ["internalRoles"],
    () => getInternalRoles(errorHandling),
    {
      enabled: immediate,
      retry: errorHandling,
    },
  );

  const createMutation = useMutation((params: { name: string }) =>
    createInternalRole({
      name: params.name,
    }),
  );

  const deleteRole = useMutation((id: string | number) => deleteRoles(id));

  return {
    roles: data,
    isLoading,
    refetch,

    createRole: createMutation.mutate,
    createRoleAsync: createMutation.mutateAsync,
    isCreatingRole: createMutation.isLoading,

    deleteRole: deleteRole.mutate,
    deleteRoleAsync: deleteRole.mutateAsync,
  };
};

export default useInternalRoles;
